import { ReactNode, useCallback } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import type { TFunction } from 'next-i18next'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import ChevronDownIcon from 'shared/icons/chevron-down-icon'
import type { CommentSortEnum } from 'modules/comments/types'
import { CommentSortOptions } from 'modules/comments/types'

interface CommentSortSelectorProps {
  currentSort: CommentSortEnum
  onSortChange: (sortOption: CommentSortEnum) => void
  className?: string
  disabled?: boolean
}

const getSortOptionLabel = (sortOption: CommentSortEnum, t: TFunction): string => {
  switch (sortOption) {
    case CommentSortOptions.NEWEST_FIRST: {
      return t('comments.sort.newest_first')
    }
    case CommentSortOptions.OLDEST_FIRST: {
      return t('comments.sort.oldest_first')
    }
    case CommentSortOptions.POPULAR: {
      return t('comments.sort.popular')
    }
    default: {
      return t('comments.sort.newest_first')
    }
  }
}

export const CommentSortSelector = ({
  currentSort,
  onSortChange,
  className,
  disabled = false,
}: CommentSortSelectorProps): ReactNode => {
  const { t } = useTranslation()

  const handleSortChange = useCallback(
    (sortOption: CommentSortEnum) => {
      onSortChange(sortOption)
    },
    [onSortChange],
  )

  const sortOptions = [
    CommentSortOptions.NEWEST_FIRST,
    CommentSortOptions.OLDEST_FIRST,
    CommentSortOptions.POPULAR,
  ]

  return (
    <div className={twMerge('flex items-center gap-4', className)}>
      <p className="font-semibold text-text-primary">{t('comments.sort.label')}</p>
      <div className="relative inline-block text-left">
        <Menu>
          <MenuButton
            disabled={disabled}
            className={twMerge(
              `flex items-center gap-2 rounded-lg border border-text-secondary/30 bg-background-secondary px-4 py-2.5 text-sm font-medium text-text-primary shadow-xs transition-colors hover:bg-text-secondary/5 focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:outline-hidden`,
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <span>{getSortOptionLabel(currentSort, t)}</span>
            <ChevronDownIcon className="size-4 text-text-primary" />
          </MenuButton>

          <MenuItems className="absolute right-0 z-20 mt-2 w-max max-w-52 min-w-32 origin-top-right rounded-lg bg-background-secondary shadow-lg ring-1 ring-black/5 focus:outline-hidden">
            <div className="py-1">
              {sortOptions.map(sortOption => (
                <MenuItem key={sortOption}>
                  {({ active }) => (
                    <button
                      onClick={() => handleSortChange(sortOption)}
                      className={twMerge(
                        `flex w-full items-center px-4 py-3 text-left text-sm transition-colors`,
                        active ? 'bg-brand-primary/5 text-text-primary' : `text-text-tertiary`,
                        currentSort === sortOption &&
                          `bg-brand-primary/10 font-semibold text-brand-primary`,
                      )}
                    >
                      {getSortOptionLabel(sortOption, t)}
                    </button>
                  )}
                </MenuItem>
              ))}
            </div>
          </MenuItems>
        </Menu>
      </div>
    </div>
  )
}
